import { ref, computed } from 'vue'
import { consola } from 'consola'
import type {
  NotificationAction,
  NotificationScreen,
  NotificationActionsResponse,
  NotificationScreensResponse,
} from '~/fsd-pages/admin/api'
import APIAdminNotification from '~/fsd-pages/admin/api'

// Logger от Nuxt (consola)
const logger = consola.withTag('notification-config')

/**
 * Глобальное состояние (singleton) - все компоненты используют одни и те же данные
 */
const globalState = {
  actions: ref<NotificationAction[]>([]),
  screens: ref<NotificationScreen[]>([]),
  isLoading: ref(false),
  error: ref<string | null>(null),
  fetchPromise: null as Promise<void> | null,
  isFetched: false,
}

/**
 * Composable для работы с конфигурацией уведомлений (actions/screens)
 * Следует принципам DRY и SOLID
 *
 * ⚠️ ВАЖНО: Использует глобальное состояние (синглтон), чтобы все компоненты делили одни данные
 *
 * API Endpoints:
 * - GET /notifications/config/actions - получить список действий (5 стандартных)
 * - GET /notifications/config/screens - получить список экранов (7 стандартных)
 * - GET /notifications/config/all - получить все справочники одним запросом (рекомендуется)
 *
 * Поддерживаемые действия (actions):
 * 1. open_product - Открыть товар (✨ поддержка промокодов с автоприменением)
 * 2. open_screen - Открыть экран
 * 3. apply_promo - Применить промокод
 * 4. resume_checkout - Вернуться к оформлению
 * 5. open_url - Открыть URL
 * 6. multiple_actions - Комбинированные действия (добавляется на фронтенде)
 *
 * Поддерживаемые экраны (screens):
 * - ProductDetails, Cart, OrderDetails, Profile, Home, Orders, Menu, Referral
 */
export function useNotificationConfig() {
  // Используем глобальное состояние - все компоненты делят одни данные
  const actions = globalState.actions
  const screens = globalState.screens
  const isLoading = globalState.isLoading
  const error = globalState.error

  /**
   * Получить список всех доступных действий
   */
  async function fetchActions(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response: NotificationActionsResponse = await APIAdminNotification.getNotificationActions()

      if (response.success) {
        actions.value = response.data || []
      } else {
        error.value = 'Не удалось загрузить список действий'
      }
    } catch (error_: any) {
      error.value = error_?.message || 'Ошибка загрузки действий'
      logger.error('Error fetching actions:', error_)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Получить список всех доступных экранов
   * @param platform - Платформа ('all' | 'web' | 'ios' | 'android')
   */
  async function fetchScreens(platform: string = 'all'): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response: NotificationScreensResponse = await APIAdminNotification.getNotificationScreens(platform)

      if (response.success) {
        screens.value = response.data || []
      } else {
        error.value = 'Не удалось загрузить список экранов'
      }
    } catch (error_: any) {
      error.value = error_?.message || 'Ошибка загрузки экранов'
      logger.error('Error fetching screens:', error_)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Получить все справочники разом
   * 
   * Использует отдельные запросы для actions и screens, так как эндпоинт /all может не работать
   * 
   * Защита от повторных вызовов (принцип DRY):
   * - Если уже идет загрузка (есть активный Promise), возвращаем его
   * - Если данные уже загружены, возвращаем успешный Promise
   * - Это предотвращает множественные запросы и зацикливание
   * 
   * @param force - Принудительная перезагрузка (игнорирует кэш)
   */
  function fetchAll(force: boolean = false): Promise<void> {
    // Если данные уже загружены и не требуется принудительная перезагрузка, просто возвращаем
    if (globalState.isFetched && !force) {
      logger.debug('Конфигурация уже загружена, пропускаем запрос')
      return Promise.resolve()
    }

    // Если уже идет загрузка, возвращаем существующий Promise
    if (globalState.fetchPromise && !force) {
      logger.debug('Загрузка уже идет, возвращаем существующий Promise')
      return globalState.fetchPromise
    }

    isLoading.value = true
    error.value = null

    // Создаем новый Promise и сохраняем его СРАЗУ, чтобы предотвратить повторные вызовы
    globalState.fetchPromise = (async () => {
      try {
        logger.debug('Загрузка конфигурации уведомлений через отдельные запросы...')
        
        // Используем отдельные запросы вместо общего /all, так как он может не работать
        const [actionsResponse, screensResponse] = await Promise.all([
          APIAdminNotification.getNotificationActions(),
          APIAdminNotification.getNotificationScreens('all'),
        ])

        logger.debug('Ответ API actions:', {
          success: actionsResponse.success,
          actionsCount: actionsResponse.data?.length ?? 0,
          actions: actionsResponse.data,
        })

        logger.debug('Ответ API screens:', {
          success: screensResponse.success,
          screensCount: screensResponse.data?.length ?? 0,
          screens: screensResponse.data,
        })

        if (actionsResponse.success) {
          actions.value = actionsResponse.data || []
        } else {
          logger.error('❌ Ошибка загрузки действий:', actionsResponse)
          actions.value = []
        }

        if (screensResponse.success) {
          screens.value = screensResponse.data || []
        } else {
          logger.error('❌ Ошибка загрузки экранов:', screensResponse)
          screens.value = []
        }

        globalState.isFetched = true
        
        if (actions.value.length === 0 && screens.value.length === 0) {
          logger.warn('⚠️ API вернул пустые массивы действий и экранов. Проверьте бэкенд.')
        } else {
          logger.success(`✅ Загружено ${actions.value.length} действий и ${screens.value.length} экранов`)
        }
      } catch (error_: any) {
        error.value = error_?.message || 'Ошибка загрузки конфигурации'
        logger.error('❌ Error fetching config:', error_)
        // Сбрасываем isFetched при ошибке, чтобы можно было повторить запрос
        globalState.isFetched = false
      } finally {
        isLoading.value = false
        globalState.fetchPromise = null
      }
    })()

    return globalState.fetchPromise
  }

  /**
   * Найти действие по коду
   */
  const getActionByCode = computed(() => (code: string): NotificationAction | undefined => {
    return actions.value.find(action => action.code === code && action.isActive)
  })

  /**
   * Найти экран по коду
   */
  const getScreenByCode = computed(() => (code: string): NotificationScreen | undefined => {
    return screens.value.find(screen => screen.code === code && screen.isActive)
  })

  /**
   * Получить только активные действия
   */
  const activeActions = computed(() => {
    return actions.value.filter(action => action.isActive)
  })

  /**
   * Получить только активные экраны
   */
  const activeScreens = computed(() => {
    return screens.value.filter(screen => screen.isActive)
  })

  return {
    // State
    actions,
    screens,
    isLoading,
    error,

    // Computed
    getActionByCode,
    getScreenByCode,
    activeActions,
    activeScreens,

    // Methods
    fetchActions,
    fetchScreens,
    fetchAll,
  }
}

